import { useCallback, useEffect, useState } from "react";

import { getJSON, putJSON } from "../lib/api";
import { useProToken } from "./useProToken";

// Copilot dock open/closed persistence (plan 188 W2-M8, task 802, D4:
// "open/closed state persists across pages AND sessions via the durable
// `user_app_state` store"). Uses the generic `/api/users/me/app-state`
// surface (see CLAUDE.md's app-state trigger-table entry) under a
// pro-scoped namespace/key, with an optimistic localStorage mirror so the
// dock doesn't flash closed on first paint while the network round-trip is
// in flight.
const NAMESPACE = "pro.copilot";
const KEY = "dock_open.v1";
const STORAGE_KEY = "sill.pro.copilot.dock_open";

type AppStateResponse = { app_state: Record<string, Record<string, unknown>> };

function readLocal(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeLocal(open: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, open ? "1" : "0");
  } catch {
    // best-effort only
  }
}

export function useCopilotDockOpen(): {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
} {
  const [open, setOpenState] = useState<boolean>(readLocal);
  const getToken = useProToken();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token) return;
      try {
        const res = await getJSON<AppStateResponse>(
          "/api/users/me/app-state",
          token,
        );
        // The app-state contract stores object values only (booleans/scalars
        // are rejected 400 by `requireJSONObject`), so the dock-open flag is
        // wrapped as `{ open: boolean }`.
        const value = res.app_state?.[NAMESPACE]?.[KEY] as
          | { open?: unknown }
          | undefined;
        if (!cancelled && typeof value?.open === "boolean") {
          setOpenState(value.open);
          writeLocal(value.open);
        }
      } catch {
        // Fail open to the localStorage mirror — never block the dock on a
        // network hiccup.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      writeLocal(next);
      (async () => {
        const token = await getToken();
        if (!token) return;
        try {
          await putJSON(
            `/api/users/me/app-state/${NAMESPACE}/${KEY}`,
            { value: { open: next } },
            token,
          );
        } catch {
          // Best-effort sync; localStorage already holds the local truth.
        }
      })();
    },
    [getToken],
  );

  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  return { open, setOpen, toggle };
}
